import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Edit3, Play, GitBranch } from "lucide-react";
import RuleModal from "../components/RuleModal";

export default function DocumentRules() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/document-rules`);
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const runAll = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_BASE}/document-rules/run-all`);
      toast.success(`Checked ${r.data.checked} docs, modified ${r.data.modified}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try { await axios.delete(`${API_BASE}/document-rules/${rule.rule_id}`); await reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="rules-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Automations</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Document <em className="italic" style={{ color: "var(--accent)" }}>rules</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2">
            Auto-move files into folders or add tags based on filename / tags / client / source.
            Rules apply on every upload and on demand via "Run all rules".
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runAll} disabled={running} className="btn-secondary" data-testid="rules-run-all">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Run all rules
          </button>
          <button onClick={() => setEditing({})} className="btn-primary" data-testid="rules-create-btn">
            <Plus size={14} /> New rule
          </button>
        </div>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="rules-empty">
          <GitBranch size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No rules yet</div>
          <div className="text-[var(--ink-3)] text-sm">Create a rule to auto-organize your firm's documents.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rules-list">
          {rows.map((r) => <RuleCard key={r.rule_id} rule={r} onEdit={() => setEditing(r)} onDelete={() => remove(r)} />)}
        </div>
      )}

      {editing && <RuleModal rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function RuleCard({ rule, onEdit, onDelete }) {
  const c = rule.conditions || {};
  const a = rule.actions || {};
  const tags = [...(c.tags_any || []).map((t) => `tag~${t}`), ...(c.tags_all || []).map((t) => `tag=${t}`)];
  return (
    <div className="card p-5" data-testid={`rule-row-${rule.rule_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="badge" style={{ background: rule.enabled ? "var(--moss)" : "var(--ink-3)", color: "var(--paper)" }}>
              {rule.enabled ? "enabled" : "disabled"}
            </span>
            <div className="font-heading text-lg" style={{ fontWeight: 500 }}>{rule.name}</div>
            <span className="text-[10px] font-mono text-[var(--ink-3)]">priority {rule.priority}</span>
          </div>
          <div className="text-[13px] text-[var(--ink-2)] mb-1">
            <b>When:</b>{" "}
            {tags.join(", ") || "(any document)"}
            {c.filename_contains && ` · filename contains "${c.filename_contains}"`}
            {c.client_id && ` · client=${c.client_id}`}
            {c.visibility && ` · ${c.visibility}-only`}
            {c.source && ` · uploaded by ${c.source}`}
          </div>
          <div className="text-[13px]">
            <b>Then:</b>{" "}
            {a.move_to_folder && <>move → <span className="font-mono">{a.move_to_folder}</span>; </>}
            {(a.add_tags || []).length > 0 && (
              <>add {(a.add_tags || []).map((t, i) => (
                <span key={t}>
                  {i > 0 ? ", " : ""}<span className="font-mono">{t}</span>
                </span>
              ))}; </>
            )}
            {(a.remove_tags || []).length > 0 && <>remove {a.remove_tags.join(", ")}; </>}
            {a.set_visibility && <>set visibility → {a.set_visibility}</>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} data-testid={`rule-edit-${rule.rule_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]">
            <Edit3 size={11} /> Edit
          </button>
          <button onClick={onDelete} data-testid={`rule-delete-${rule.rule_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-3)] hover:text-[var(--accent)]">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
